import React from 'react';

import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { useBaseWidget } from 'hooks/useBaseWidget';

import { ColorTextByValue } from 'components/common/ColorTextByValue/ColorTextByValue';
import constants from 'components/common/Constants/constants';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { ShareInsightButton } from 'components/common/ShareInsightButton/ShareInsightButton';
import SwitchComponent from 'components/common/Switch/Switch';
import { prepareVersionSelectTitle } from 'components/common/TreeProgramFilter/VersionSelect/versionSelect.utils';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { portfolioConfig } from 'components/Dashboards/Portfolio/portfolioConfig';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import {
    ILocalFilters,
    ISetSubTitleForExport,
} from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { projectConfig } from 'components/Dashboards/Project/ProjectWrapper/projectConfig';
import { useTimelineAndDensityLogic } from 'components/widgets/timelineAndDensity/TimelineAndDensity.logic';
import { useTimelineAndDensityProvider } from 'components/widgets/timelineAndDensity/TimelineAndDensity.provider';
import { HeatmapWrapper } from 'components/widgets/timelineAndDensity/components/heatmapWrapper/heatmapWrapper';
import classes from 'components/widgets/timelineAndDensity/timelineAndDensity.module.scss';

const id = 'portfolioTimelineAndDensity';
const title = 'Portfolio Timeline & Density';
const componentKey = 'timelineAndDensity';

export interface Props {
    widgetId?: string;
    externalComponents?: React.ReactNode;
    editNarrative?: React.ReactNode;
    setSubTitleForExport?: ISetSubTitleForExport;
    localFilters?: ILocalFilters;
}

export const TimelineAndDensity = ({
    widgetId,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    localFilters,
}: Props) => {
    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId: undefined,
        contractId: undefined,
        latestVersionId: undefined,
        title,
        setSubTitleForExport,
        customSubTitle: `${portfolioConfig.portfolioOverview.title}, ${prepareVersionSelectTitle()}`,
    });
    const { ui, api } = useTimelineAndDensityProvider({
        widgetId,
        localFilters,
    });
    const { logic } = useTimelineAndDensityLogic();

    const data = api.getTimelineAndDensity.data;
    const isLoading = api.getTimelineAndDensity.isLoading;

    return (
        <WidgetWithTitle
            id={id}
            title={title}
            tooltip={data?.info}
            titleComponents={[
                <div key={'density'}>
                    Show Density
                    <SwitchComponent onChange={logic.handleShowDensity} checked={logic.isShowDensity} />
                </div>,
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    widgetId={widgetId}
                    route={route}
                    projectId={null}
                    contractId={null}
                    localFilters={ui.state}
                />,
                <ShareInsightButton key={'shareInsight'} title={title} link={route} />,
                <ExportDropdown title={title} key={'export'} subTitle={subTitle} />,
            ]}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && ui.pills.length > 0 && (
                    <PinboardWidgetPills key={'widgetFilters'} widgetId={widgetId} filters={ui.pills} />
                ),
            ]}
        >
            {data && data.heatMapPortfolioProjectResponse.length === 0 ? (
                <NoData />
            ) : (
                <div className={classes.timelineAndDensityContainer}>
                    {isLoading && <OverlayWithSpinner />}
                    <div className={classes.titleContainer}>
                        {data?.heatMapPortfolioProjectResponse.map((item, index) => {
                            const first = index === 0;

                            return (
                                <div className={classes.title} style={{ height: first ? 170 : 120 }} key={item.id}>
                                    {item.versionHashCode ? (
                                        <>
                                            <Link
                                                data-testid={'link-lable'}
                                                to={AppRoutes.buildPath(
                                                    AppRoutes.DASHBOARD_PROJECT_PATH,
                                                    projectConfig.criticalityAnalysis.link,
                                                    item.versionHashCode,
                                                )}
                                                className={classes.linkLabel}
                                            >
                                                {item.label}
                                            </Link>
                                            <div>
                                                Submitted Completion:{' '}
                                                <span className={classes.submittedCompletionValue}>
                                                    {item.completionPercent}%
                                                </span>
                                            </div>
                                            <div>
                                                Schedule Performance (SPI):{' '}
                                                {item.preferredSpiMethod === 'CriticalityWeighted' ? (
                                                    <ColorTextByValue value={item.spiCriticality} />
                                                ) : (
                                                    <ColorTextByValue value={item.spiMilestone} />
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <div className={classes.label}>{item.label}</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className={classes.mapContainer} ref={logic.mapContainerRef}>
                        <div className={classes.todayLine} ref={logic.todayLineRef}>
                            <div className={classes.dateBox}>
                                {moment(logic.todayLineDate).format(constants.formats.date.monthAndYear)}
                            </div>
                        </div>
                        {data &&
                            data.heatMapPortfolioProjectResponse.map((item, index) => {
                                const first = index === 0;
                                return (
                                    <div key={item.id} className={classes.mapWrapper}>
                                        <HeatmapWrapper
                                            mainData={data.heatMapPortfolioProjectResponse[0].data}
                                            data={data.heatMapPortfolioProjectResponse[index].data}
                                            isFirst={first}
                                            height={first ? 147 : 72}
                                            callback={logic.setTodayLine}
                                            todayLineDate={logic.todayLineDate}
                                            isShowDensity={logic.isShowDensity}
                                            isBaseline={false}
                                        />

                                        {data.heatMapPortfolioProjectResponse[index].baselineData.length !== 0 && (
                                            <div className={classes.baseline}>
                                                <HeatmapWrapper
                                                    mainData={data.heatMapPortfolioProjectResponse[0].data}
                                                    data={data.heatMapPortfolioProjectResponse[index].baselineData}
                                                    height={10}
                                                    isShowDensity={logic.isShowDensity}
                                                    isBaseline={true}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                    </div>
                </div>
            )}
        </WidgetWithTitle>
    );
};
